/**
 * Swiss input mask utilities.
 *
 * Each format function takes a raw (possibly partially typed) string and
 * returns a clean, consistently formatted string. They are idempotent — safe
 * to call on already-formatted values.
 *
 * Zod refinement helpers are co-located here so validation rules stay in sync
 * with the format functions.
 */

export type MaskType = 'ahv' | 'iban_ch' | 'phone_ch';

// ── Format functions ──────────────────────────────────────────────────────────

/**
 * AHV / AVS number  →  756.XXXX.XXXX.XX
 * Swiss social security numbers always start with 756.
 * The prefix is enforced automatically — users only type the 10 digits after it.
 */
export function formatAHV(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (!digits) return '';
  // Always force 756 prefix; take up to 10 further digits
  const local = digits.startsWith('756') ? digits.slice(3, 13) : digits.slice(0, 10);
  const all = '756' + local;
  if (all.length <= 3) return '756.';
  if (all.length <= 7) return `756.${all.slice(3)}`;
  if (all.length <= 11) return `756.${all.slice(3, 7)}.${all.slice(7)}`;
  return `756.${all.slice(3, 7)}.${all.slice(7, 11)}.${all.slice(11)}`;
}

/**
 * Swiss IBAN  →  CH## XXXX XXXX XXXX XXXX X
 * Strips spaces, uppercases, groups in blocks of 4 (21 chars total).
 */
export function formatIBAN(input: string): string {
  const clean = input.replace(/\s/g, '').toUpperCase().slice(0, 21);
  return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
}

/**
 * Swiss phone number — always normalises to international +41 XX XXX XX XX.
 * Local 0XX numbers are automatically converted (078 → +41 78).
 * Non-Swiss numbers (other country code) are left unchanged.
 */
export function formatPhone(input: string): string {
  const hasPlus = input.trimStart().startsWith('+');
  const digits = input.replace(/\D/g, '');

  // Determine local 9-digit part
  let local: string;
  if (hasPlus && digits.startsWith('41')) {
    local = digits.slice(2).replace(/^0+/, '').slice(0, 9);
  } else if (!hasPlus && digits.startsWith('0')) {
    // Convert 0XX local → +41 XX
    local = digits.slice(1, 10);
  } else {
    // Non-Swiss or unknown — return as typed
    return input;
  }

  let out = '+41';
  if (local.length > 0) out += ` ${local.slice(0, 2)}`;
  if (local.length > 2) out += ` ${local.slice(2, 5)}`;
  if (local.length > 5) out += ` ${local.slice(5, 7)}`;
  if (local.length > 7) out += ` ${local.slice(7, 9)}`;
  return out;
}

// ── Dispatch ──────────────────────────────────────────────────────────────────

export function applyMask(value: string, maskType: MaskType): string {
  switch (maskType) {
    case 'ahv':      return formatAHV(value);
    case 'iban_ch':  return formatIBAN(value);
    case 'phone_ch': return formatPhone(value);
  }
}

// ── Placeholders ──────────────────────────────────────────────────────────────

export const MASK_PLACEHOLDERS: Record<MaskType, string> = {
  ahv:      '756.0000.0000.00',
  iban_ch:  'CH00 0000 0000 0000 0000 0',
  phone_ch: '+41 79 123 45 67',
};

// ── Zod validators (use with .refine()) ───────────────────────────────────────

/**
 * AHV: 756.XXXX.XXXX.XX — validates format AND EAN-13 check digit.
 * A copy of the authoritative check in backend/utils/ahvNumber.js, which the employee
 * schemas enforce; this one only gives the user the error before the round trip.
 * The 13th digit is computed using the standard EAN-13 algorithm:
 * alternating weights 1 and 3, modulo 10.
 */
export function validateAHV(val: string | undefined): boolean {
  if (!val) return true; // optional
  if (!/^\d{3}\.\d{4}\.\d{4}\.\d{2}$/.test(val)) return false;
  const digits = val.replace(/\D/g, '');
  const weights = [1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(digits[i]) * weights[i];
  const expected = (10 - (sum % 10)) % 10;
  return expected === parseInt(digits[12]);
}

/** Swiss IBAN: CH + 2 check digits + 17 alphanumeric chars = 21 chars */
export function validateIBAN(val: string | undefined): boolean {
  if (!val) return true;
  const stripped = val.replace(/\s/g, '');
  return /^CH\d{2}[A-Z0-9]{17}$/.test(stripped);
}

/** Swiss phone: +41 format or 0XX format, 9–15 chars including spaces */
export function validatePhone(val: string | undefined): boolean {
  if (!val) return true;
  return /^(\+41\s?\d{2}\s?\d{3}\s?\d{2}\s?\d{2}|0\d{2}\s?\d{3}\s?\d{2}\s?\d{2})$/.test(val.trim());
}
